'use client';
import { useState } from 'react';
import Icon from '@/components/ui/AppIcon';

const projectTypes = [
  'Full Stack Web App',
  'Mobile Application',
  'UI/UX Design',
  'Brand Identity',
  'E-Commerce Store',
  'Cloud & DevOps',
  'Video & Animation',
  'Technical Leadership',
  'Other',
];

const budgetRanges = [
  '$500 – $1,000',
  '$1,000 – $5,000',
  '$5,000 – $15,000',
  '$15,000 – $30,000',
  '$30,000+',
  "Let's discuss",
];

const timelines = [
  'ASAP (Rush)',
  '1–2 Weeks',
  '1 Month',
  '2–3 Months',
  '3–6 Months',
  'Ongoing Partnership',
];

interface FormData {
  name: string;
  email: string;
  company: string;
  projectType: string;
  budget: string;
  timeline: string;
  message: string;
  phone: string;
}

export default function ContactSection() {
  const [form, setForm] = useState<FormData>({
    name: '',
    email: '',
    company: '',
    projectType: '',
    budget: '',
    timeline: '',
    message: '',
    phone: '',
  });
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    const formData = new FormData();
    Object.entries(form).forEach(([key, value]) => {
      formData.append(key, value);
    });
    // Aapki Web3Forms Key
    formData.append('access_key', '42f7d84f-9989-4c5d-879a-24bf9d721bc6');

    try {
      const response = await fetch('https://api.web3forms.com/submit', {
        method: 'POST',
        body: formData,
      });

      const data = await response.json();

      if (data.success) {
        setSubmitted(true);
      } else {
        alert('Something went wrong. Please try again.');
      }
    } catch (error) {
      console.error('Error submitting form:', error);
      alert('Submission failed. Check your connection.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="contact" className="py-16 lg:py-20 relative">
      {/* Glow */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          background:
            'radial-gradient(ellipse 60% 60% at 50% 100%, rgba(108,99,255,0.08) 0%, transparent 70%)',
        }}
      />

      <div className="max-w-7xl mx-auto px-6 relative z-10">
        {/* Header */}
        <div className="text-center mb-16 initially-hidden animate-in">
          <div className="section-label mb-3">Get In Touch</div>
          <h2
            className="text-4xl md:text-6xl font-extrabold mb-4"
            style={{ color: 'var(--text-primary)', letterSpacing: '-0.04em' }}
          >
            Start Your <span className="gradient-text">Next Project</span>
          </h2>
          <p className="text-lg max-w-2xl mx-auto" style={{ color: 'var(--text-secondary)' }}>
            Ready to build something extraordinary? Tell me about your project and let&apos;s create
            something the world hasn&apos;t seen yet.
          </p>
        </div>

        <div className="grid lg:grid-cols-5 gap-10">
          {/* Left Side (Details) remains same */}
          <div className="lg:col-span-2 space-y-6 initially-hidden animate-in-left">
            {[
              {
                icon: 'EnvelopeIcon',
                label: 'Email',
                value: 'akif.tm@gmail.com',
                color: '#6C63FF',
                href: 'mailto:akif.tm@gmail.com',
              },
              {
                icon: 'PhoneIcon',
                label: 'Phone / WhatsApp',
                value: '+92 (334) 337-4983',
                color: '#10D9A0',
                href: 'https://wa.me/923343374983',
              },
              {
                icon: 'MapPinIcon',
                label: 'Location',
                value: 'Open to Remote Work (Worldwide)',
                color: '#00D4FF',
                href: '#',
              },
              {
                icon: 'ClockIcon',
                label: 'Response Time',
                value: 'Within a few hours (Business hours)',
                color: '#F59E0B',
                href: '#',
              },
            ].map(({ icon, label, value, color, href }) => (
              <a
                key={label}
                href={href}
                className="flex items-center gap-4 glass-card p-5 no-underline"
                style={{ textDecoration: 'none' }}
              >
                <div
                  className="w-12 h-12 rounded-2xl flex items-center justify-center flex-shrink-0"
                  style={{ background: `${color}20` }}
                >
                  <Icon name={icon as 'EnvelopeIcon'} size={22} style={{ color }} />
                </div>
                <div>
                  <div
                    className="text-xs font-semibold mb-0.5"
                    style={{ color: 'var(--text-muted)' }}
                  >
                    {label}
                  </div>
                  <div className="text-sm font-semibold" style={{ color: 'var(--text-primary)' }}>
                    {value}
                  </div>
                </div>
              </a>
            ))}

            {/*

            <div
              className="glass-card p-6 gradient-border"
              style={{
                background: 'linear-gradient(135deg, rgba(108,99,255,0.1), rgba(0,212,255,0.05))',
              }}
            >
              <div className="flex items-center gap-3 mb-4">
                <Icon name="DocumentArrowDownIcon" size={24} className="text-[#6C63FF]" />
                <span className="font-bold" style={{ color: 'var(--text-primary)' }}>
                  Download My CV
                </span>
              </div>
              <p className="text-sm mb-5" style={{ color: 'var(--text-secondary)' }}>
                Full resume with work history, skills, certifications, and portfolio highlights.
              </p>
              <a
                href="javascript:void(0)"
                download
                className="btn-primary flex items-center gap-2 justify-center w-full relative z-10"
                style={{ textDecoration: 'none' }}
              >
                <Icon name="ArrowDownTrayIcon" size={18} />
                <span>Download CV (PDF)</span>
              </a>
            </div>

            */}

            <div
              className="glass-card p-6 gradient-border"
              style={{
                background: 'linear-gradient(135deg, rgba(37,211,102,0.1), rgba(37,211,102,0.05))',
              }}
            >
              <div className="flex items-center gap-3 mb-4">
                <Icon name="ChatBubbleLeftRightIcon" size={24} className="text-[#25D366]" />
                <span className="font-bold" style={{ color: 'var(--text-primary)' }}>
                  Chat on WhatsApp
                </span>
              </div>

              <p className="text-sm mb-5" style={{ color: 'var(--text-secondary)' }}>
                Have a project in mind? Let’s connect on WhatsApp to discuss your idea,
                requirements, and how we can bring your project to life.
              </p>

              <a
                href="https://wa.me/923343374983?text=Hello%20Akif%2C%20I%20would%20like%20to%20discuss%20a%20project."
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2 justify-center px-6 py-3 rounded-full font-semibold"
                style={{
                  background: 'rgba(37,211,102,0.1)', // WhatsApp green shade
                  border: '1px solid rgba(37,211,102,0.3)',
                  color: '#25D366',
                  transition: 'all 0.3s ease',
                }}
              >
                <Icon name="ChatBubbleLeftRightIcon" size={18} />
                <span>Message on WhatsApp</span>
              </a>
            </div>

            <div className="glass-card p-5 flex items-center gap-4">
              <div className="availability-dot" />
              <div>
                <div className="font-bold text-sm" style={{ color: '#10D9A0' }}>
                  Available for New Projects
                </div>
                <div className="text-xs" style={{ color: 'var(--text-muted)' }}>
                  Currently accepting projects starting March 2026
                </div>
              </div>
            </div>
          </div>

          {/* Right: Booking Form */}
          <div className="lg:col-span-3 initially-hidden animate-in-right">
            {submitted ? (
              <div
                className="glass-card p-12 text-center gradient-border h-full flex flex-col items-center justify-center"
                style={{ minHeight: '500px' }}
              >
                <div
                  className="w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-6"
                  style={{
                    background: 'rgba(16,217,160,0.15)',
                    animation: 'pulse-glow 2s ease-in-out infinite',
                  }}
                >
                  <Icon name="CheckCircleIcon" size={40} className="text-[#10D9A0]" />
                </div>
                <h3 className="text-2xl font-bold mb-3" style={{ color: 'var(--text-primary)' }}>
                  Message Received!
                </h3>
                <p className="text-base max-w-sm" style={{ color: 'var(--text-secondary)' }}>
                  Thank you for reaching out. I&apos;ll review your project details and get back to
                  you soon.
                </p>
                <button className="btn-ghost mt-8" onClick={() => setSubmitted(false)}>
                  Send Another Message
                </button>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="glass-card p-8 space-y-5 gradient-border">
                {/* Hidden input for Web3Forms */}
                <input
                  type="hidden"
                  name="access_key"
                  value="42f7d84f-9989-4c5d-879a-24bf9d721bc6"
                />

                <h3 className="text-xl font-bold mb-6" style={{ color: 'var(--text-primary)' }}>
                  Project Booking Form
                </h3>

                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <label
                      className="block text-xs font-semibold mb-2"
                      style={{ color: 'var(--text-muted)' }}
                    >
                      Full Name *
                    </label>
                    <input
                      type="text"
                      name="name"
                      required
                      value={form.name}
                      onChange={handleChange}
                      placeholder="John Smith"
                      className="form-input"
                    />
                  </div>
                  <div>
                    <label
                      className="block text-xs font-semibold mb-2"
                      style={{ color: 'var(--text-muted)' }}
                    >
                      Email Address *
                    </label>
                    <input
                      type="email"
                      name="email"
                      required
                      value={form.email}
                      onChange={handleChange}
                      placeholder="john@company.com"
                      className="form-input"
                    />
                  </div>
                </div>

                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <label
                      className="block text-xs font-semibold mb-2"
                      style={{ color: 'var(--text-muted)' }}
                    >
                      Company / Organization
                    </label>
                    <input
                      type="text"
                      name="company"
                      value={form.company}
                      onChange={handleChange}
                      placeholder="Acme Corp"
                      className="form-input"
                    />
                  </div>
                  <div>
                    <label
                      className="block text-xs font-semibold mb-2"
                      style={{ color: 'var(--text-muted)' }}
                    >
                      Phone / WhatsApp
                    </label>
                    <input
                      type="tel"
                      name="phone"
                      value={form.phone}
                      onChange={handleChange}
                      placeholder="+1 (555) 000-0000"
                      className="form-input"
                    />
                  </div>
                </div>

                <div>
                  <label
                    className="block text-xs font-semibold mb-2"
                    style={{ color: 'var(--text-muted)' }}
                  >
                    Project Type *
                  </label>
                  <select
                    name="projectType"
                    required
                    value={form.projectType}
                    onChange={handleChange}
                    className="form-input"
                  >
                    <option value="">Select project type...</option>
                    {projectTypes.map((t) => (
                      <option key={t} value={t}>
                        {t}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <label
                      className="block text-xs font-semibold mb-2"
                      style={{ color: 'var(--text-muted)' }}
                    >
                      Budget Range
                    </label>
                    <select
                      name="budget"
                      value={form.budget}
                      onChange={handleChange}
                      className="form-input"
                    >
                      <option value="">Select budget...</option>
                      {budgetRanges.map((b) => (
                        <option key={b} value={b}>
                          {b}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label
                      className="block text-xs font-semibold mb-2"
                      style={{ color: 'var(--text-muted)' }}
                    >
                      Timeline
                    </label>
                    <select
                      name="timeline"
                      value={form.timeline}
                      onChange={handleChange}
                      className="form-input"
                    >
                      <option value="">Select timeline...</option>
                      {timelines.map((tl) => (
                        <option key={tl} value={tl}>
                          {tl}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                <div>
                  <label
                    className="block text-xs font-semibold mb-2"
                    style={{ color: 'var(--text-muted)' }}
                  >
                    Project Details *
                  </label>
                  <textarea
                    name="message"
                    required
                    rows={5}
                    value={form.message}
                    onChange={handleChange}
                    placeholder="Tell me about your project..."
                    className="form-input resize-none"
                  />
                </div>

                <button
                  type="submit"
                  disabled={loading}
                  className="btn-primary w-full flex items-center justify-center gap-3 relative z-10"
                  style={{ padding: '16px', fontSize: '1rem' }}
                >
                  {loading ? (
                    <>
                      <div className="w-5 h-5 rounded-full border-2 border-white/30 border-t-white animate-spin" />
                      <span>Sending...</span>
                    </>
                  ) : (
                    <>
                      <Icon name="PaperAirplaneIcon" size={20} />
                      <span>Send Project Brief</span>
                    </>
                  )}
                </button>
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
